import { compact } from 'lodash';
import { ReactElement, useMemo } from 'react';

import { TranslatableText } from '@dreryk/react-common';
import { NavTabs } from '@dreryk/react-components';

import { ServiceSettingsPanel } from '../serviceSettings/ServiceSettingsPanel';
import { MedicalServicePanel } from '../medicalService/MedicalServicePanel';
import { ReceptionServicePanel } from '../receptionService/ReceptionServicePanel';
import { PrivateMedialServiceNavParams } from '../routes';
import { entryTranslationKeys } from '../translations';

import { PrivateMedicalServiceActionBar } from './PrivateMedicalServiceActionBar';

type PrivateMedicalServiceNavTabProps = {
   param: PrivateMedialServiceNavParams;
   label: TranslatableText;
   content: ReactElement;
};

const useTabs = (
   hasMorePractitioners: boolean,
   onlinePaymentsIntegrationExists: boolean
): PrivateMedicalServiceNavTabProps[] => {
   return useMemo(
      () =>
         compact([
            {
               param: PrivateMedialServiceNavParams.RECEPTION_SERVICE,
               label: entryTranslationKeys.tabs.receptionService,
               content: <ReceptionServicePanel hasMorePractitioners={hasMorePractitioners} />,
            },
            {
               param: PrivateMedialServiceNavParams.SETTLEMENT_SERVICE,
               label: entryTranslationKeys.tabs.settlementService,
               content: <MedicalServicePanel hasMorePractitioners={hasMorePractitioners} />,
            },
            onlinePaymentsIntegrationExists
               ? {
                    param: PrivateMedialServiceNavParams.SERVICE_SETTINGS,
                    label: entryTranslationKeys.tabs.serviceSettings,
                    content: <ServiceSettingsPanel />,
                 }
               : null,
         ]),
      [hasMorePractitioners, onlinePaymentsIntegrationExists]
   );
};

type PrivateMedicalServiceNavTabsProps = {
   readonly hasMorePractitioners: boolean;
   readonly onlinePaymentsIntegrationExists: boolean;
};

export const PrivateMedicalServiceNavTabs = (props: PrivateMedicalServiceNavTabsProps): JSX.Element => {
   const tabs = useTabs(props.hasMorePractitioners, props.onlinePaymentsIntegrationExists);

   return (
      <PrivateMedicalServiceActionBar>
         <NavTabs<PrivateMedialServiceNavParams> testId={'privateMedicalService'}>
            {tabs.map((tab) => (
               <NavTabs.Tab key={tab.param} testId={tab.param} param={tab.param} label={tab.label}>
                  {tab.content}
               </NavTabs.Tab>
            ))}
         </NavTabs>
      </PrivateMedicalServiceActionBar>
   );
};
